import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { analyticsAPI, suppliersAPI } from '../api/endpoints.js'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../contexts/ToastContext'
import { testBackendConnection, testAuthEndpoint } from '../utils/testConnection'
import ThemeToggle from '../components/ui/ThemeToggle'

function HomePage() {
  const { isAuthenticated, user, login, logout } = useAuth()
  const { addToast } = useToast()
  const navigate = useNavigate()
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loginForm, setLoginForm] = useState({ email: '', password: '' })
  const [showLogin, setShowLogin] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState(null)

  useEffect(() => {
    if (isAuthenticated) {
      loadData()
    } else {
      setLoading(false)
    }
  }, [isAuthenticated])

  const loadData = async () => {
    try {
      setLoading(true)
      const [dashboardRes, suppliersRes] = await Promise.all([
        analyticsAPI.getDashboard(),
        suppliersAPI.getAll()
      ])
      
      console.log('=== DASHBOARD CARREGADO ===')
      console.log('Resposta completa:', dashboardRes)
      console.log('Total de cotações:', dashboardRes.data?.totalQuotations)
      console.log('Total de fornecedores (my-suppliers):', suppliersRes.data?.length)
      console.log('===========================')
      
      // Usar contagem correta de fornecedores da rota my-suppliers
      setDashboardData({
        ...dashboardRes.data,
        totalSuppliers: suppliersRes.data?.length || 0
      })
    } catch (error) {
      console.error('Error loading data:', error)
      addToast('Erro ao carregar dados', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    
    try {
      await login(loginForm)
      addToast('Login realizado com sucesso!', 'success')
      setShowLogin(false)
      setLoginForm({ email: '', password: '' })
    } catch (error) {
      console.error('Erro detalhado do login:', error)
      addToast(error.message || 'Erro ao fazer login', 'error')
    }
  }

  const handleLogout = async () => {
    await logout()
    addToast('Logout realizado com sucesso!', 'success')
    setDashboardData(null)
  }

  const clearTestData = () => {
    localStorage.clear()
    sessionStorage.clear()
    addToast('Dados de teste limpos com sucesso!', 'success')
    window.location.reload()
  }

  const testConnection = async () => {
    setConnectionStatus('testing')
    const result = await testBackendConnection()
    setConnectionStatus(result)
    
    if (result.success) {
      addToast('Conexão com backend OK!', 'success')
    } else {
      addToast(`Erro de conexão: ${result.message}`, 'error')
    }
  }


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary flex items-center justify-center transition-colors">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-dark-accent-green mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-dark-text-secondary">Carregando...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary flex items-center justify-center transition-colors">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-800 dark:text-dark-text-primary mb-2">Sistema de Cotações</h1>
            <p className="text-gray-600 dark:text-dark-text-secondary">Faça login para acessar o sistema</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            {/* Status de Conexão */}
            <div className="mb-4 p-3 bg-gray-50 dark:bg-dark-bg-primary rounded-md transition-colors">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-dark-text-primary">Status do Backend:</span>
                <button
                  onClick={testConnection}
                  disabled={connectionStatus === 'testing'}
                  className="px-3 py-1 text-xs bg-blue-100 dark:bg-dark-accent-green/20 text-blue-700 dark:text-dark-accent-green rounded hover:bg-blue-200 dark:hover:bg-dark-accent-green/30 disabled:opacity-50 transition-colors"
                >
                  {connectionStatus === 'testing' ? 'Testando...' : 'Testar Conexão'}
                </button>
              </div>
              {connectionStatus && connectionStatus !== 'testing' && (
                <div className={`mt-2 text-xs ${
                  connectionStatus.success ? 'text-green-600' : 'text-red-600'
                }`}>
                  {connectionStatus.message}
                  {!connectionStatus.success && (
                    <div className="mt-1 text-gray-500 dark:text-dark-text-muted">
                      Verifique se o backend está rodando em http://localhost:3001
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary mb-1">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-md bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-dark-accent-green transition-colors"
                  placeholder="admin@pedidofacil.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary mb-1">
                  Senha
                </label>
                <input
                  type="password"
                  required
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-md bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-dark-accent-green transition-colors"
                  placeholder="admin123"
                />
              </div>
              <button
                type="submit"
                disabled={connectionStatus && !connectionStatus.success}
                className="w-full bg-blue-600 dark:bg-dark-accent-green text-white py-2 px-4 rounded-md hover:bg-blue-700 dark:hover:bg-dark-accent-greenDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Entrar
              </button>
            </form>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-dark-text-secondary mb-2">
              Para acessar cotações como fornecedor, use o link único recebido por email
            </p>
            <div className="text-xs text-gray-400 dark:text-dark-text-muted bg-gray-50 dark:bg-dark-bg-secondary p-2 rounded border border-gray-200 dark:border-dark-border-primary transition-colors">
              <strong>Credenciais de teste sugeridas:</strong><br/>
              Email: admin@pedidofacil.com | Senha: admin123<br/>
              Email: user@example.com | Senha: password123
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary py-8 transition-colors">
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md dark:shadow-none p-6 mb-6 border border-gray-200 dark:border-dark-border-primary transition-colors">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 dark:text-dark-text-primary mb-2">Sistema de Cotações</h1>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                Bem-vindo, {user?.name || 'Usuário'}!
              </p>
            </div>
            <div className="flex items-center gap-4">
              <ThemeToggle />
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 dark:bg-red-600 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-700 transition-colors"
              >
                Sair
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md dark:shadow-none p-6 border border-gray-200 dark:border-dark-border-primary transition-colors">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-text-primary mb-2">Cotações</h3>
            <p className="text-3xl font-bold text-blue-600 dark:text-dark-accent-green">{dashboardData?.totalQuotations || 0}</p>
            <p className="text-sm text-gray-500 dark:text-dark-text-muted">Total de cotações</p>
          </div>
          <div 
            onClick={() => navigate('/suppliers')}
            className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md dark:shadow-none p-6 border border-gray-200 dark:border-dark-border-primary transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary hover:border-blue-300 dark:hover:border-dark-accent-green"
          >
            <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-text-primary mb-2">Fornecedores</h3>
            <p className="text-3xl font-bold text-green-600 dark:text-dark-accent-greenLight">{dashboardData?.totalSuppliers || 0}</p>
            <p className="text-sm text-gray-500 dark:text-dark-text-muted">Fornecedores cadastrados</p>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md dark:shadow-none p-6 mb-6 border border-gray-200 dark:border-dark-border-primary transition-colors">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-dark-text-primary mb-4">Cotações Recentes</h2>
          
          {!dashboardData?.recentQuotations || dashboardData.recentQuotations.length === 0 ? (
            <p className="text-gray-500 dark:text-dark-text-secondary">Nenhuma cotação encontrada.</p>
          ) : (
            <div className="space-y-4">
              {dashboardData.recentQuotations.map((quotation) => (
                <div 
                  key={quotation.id} 
                  className="border border-gray-200 dark:border-dark-border-primary rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary hover:border-blue-300 dark:hover:border-dark-accent-green transition-colors cursor-pointer"
                  onClick={() => navigate(`/quotation/${quotation.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 dark:text-dark-text-primary">{quotation.client_name}</h3>
                      <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-2">
                        Cotação #{quotation.id}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-dark-text-muted">
                        Solicitada em: {new Date(quotation.request_date).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <div className="ml-4 flex items-center">
                      <svg className="w-5 h-5 text-gray-400 dark:text-dark-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md dark:shadow-none p-6 border border-gray-200 dark:border-dark-border-primary transition-colors">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-dark-text-primary mb-4">Controles do Sistema</h2>
          <div className="flex gap-4">
            <button
              onClick={loadData}
              className="px-6 py-3 bg-blue-600 dark:bg-dark-accent-green text-white font-medium rounded-md hover:bg-blue-700 dark:hover:bg-dark-accent-greenDark transition-colors"
            >
              🔄 Atualizar Dados
            </button>
            <button
              onClick={clearTestData}
              className="px-6 py-3 bg-red-600 dark:bg-red-600 text-white font-medium rounded-md hover:bg-red-700 dark:hover:bg-red-700 transition-colors"
            >
              🗑️ Limpar Cache
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-dark-text-secondary mt-2">
            Use estes controles para gerenciar os dados do sistema.
          </p>
        </div>

        <div className="mt-8 text-center text-gray-500 dark:text-dark-text-muted text-sm">
          <p>Sistema PedidoFácil - Integrado com Backend</p>
        </div>
      </div>
    </div>
  )
}

export default HomePage